import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, ArrowRight } from 'lucide-react';
import { useLang } from '../i18n';

const API = process.env.REACT_APP_BACKEND_URL;

const sessionId = (() => {
  let id = localStorage.getItem('intervantis_chat_session');
  if (!id) {
    id = 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('intervantis_chat_session', id);
  }
  return id;
})();

const ChatWidget = ({ onQuote, viewContext = '' }) => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const [pos, setPos] = useState(null);
  const dragRef = useRef({ dragging: false, moved: false, offX: 0, offY: 0, startX: 0, startY: 0 });

  const onPointerDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { dragging: true, moved: false, offX: e.clientX - rect.left, offY: e.clientY - rect.top, startX: e.clientX, startY: e.clientY };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 6) return;
    d.moved = true;
    const size = 64;
    const x = Math.max(8, Math.min(window.innerWidth - size - 8, e.clientX - d.offX));
    const y = Math.max(8, Math.min(window.innerHeight - size - 8, e.clientY - d.offY));
    setPos({ x, y });
  };
  const onPointerUp = () => {
    const wasMoved = dragRef.current.moved;
    dragRef.current.dragging = false;
    if (!wasMoved) setOpen((v) => !v);
  };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', content: t('assistant.greeting') }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setLoading(true);
    const startedAt = Date.now();
    // Make Dom feel like it's really typing: keep the indicator up for 1-2s minimum.
    const minTyping = 1000 + Math.random() * 1000;
    const pricingIntent = /\b(pric(e|ing)|cost|costs|quot(e|ation)|rate|rates|how much|budget|expensive|charge|fee|fees)\b|preis|kosten|angebot|kostet|wie ?viel|tarif|gebühr/i.test(text);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text, lang, view_context: viewContext }),
      });
      if (!res.ok) throw new Error('bad');
      const data = await res.json();
      const wait = Math.max(0, minTyping - (Date.now() - startedAt));
      if (wait) await new Promise((r) => setTimeout(r, wait));
      setMessages((m) => {
        const userCount = m.filter((x) => x.role === 'user').length;
        const next = [...m, { role: 'assistant', content: data.reply }];
        const justHadCta = m[m.length - 1]?.role === 'cta';
        if (pricingIntent && !justHadCta) next.push({ role: 'cta' });
        else if (userCount === 6 || (userCount >= 20 && userCount % 10 === 0)) next.push({ role: 'cta' });
        return next;
      });
    } catch {
      const wait = Math.max(0, minTyping - (Date.now() - startedAt));
      if (wait) await new Promise((r) => setTimeout(r, wait));
      setMessages((m) => [...m, { role: 'assistant', content: t('assistant.error') }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher (draggable) — hidden while panel is open */}
      {!open && (
        <div
          data-testid="chat-launcher-wrap"
          className={`fixed z-[110] flex items-center ${pos ? '' : 'bottom-28 right-5 lg:bottom-8 lg:right-8'}`}
          style={pos ? { left: pos.x, top: pos.y } : undefined}
        >
          {!pos && (
            <div className="hidden sm:flex items-center mr-2 select-none">
              <div className="bg-navy-deep text-white label-mono text-[11px] px-4 py-2.5 rounded-full shadow-lg shadow-black/30 border border-white/10 animate-pulse">
                {t('assistant.cta')}
              </div>
              <div className="w-2 h-2 bg-navy-deep border-r border-t border-white/10 rotate-45 -ml-1" />
            </div>
          )}
          <button
            data-testid="chat-launcher"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            aria-label={t('assistant.open')}
            className="w-16 h-16 rounded-full bg-white ring-2 ring-ember/60 shadow-lg shadow-ember/30 flex items-center justify-center transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-ember overflow-hidden p-2 touch-none cursor-grab active:cursor-grabbing"
          >
            <img src="/brand/intervantis-mark.png" alt="Dom" className="w-full h-full object-contain pointer-events-none animate-dom-spin" draggable="false" />
          </button>
        </div>
      )}

      {/* Tap-outside backdrop — mobile & tablet only */}
      {open && (
        <div
          data-testid="chat-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[105] lg:hidden bg-navy-deep/40"
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="animate-chat-pop fixed bottom-28 right-4 lg:bottom-24 lg:right-8 z-[110] w-[calc(100vw-2rem)] sm:w-[384px] h-[560px] max-h-[70vh] flex flex-col rounded-3xl overflow-hidden border border-white/12 bg-gradient-to-b from-[#0d1730] to-[#080f1f] shadow-[0_30px_80px_rgba(0,0,0,0.65)]"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-navy-deep to-[#0e2436]">
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden p-1.5 ring-2 ring-ember/40">
                <img src="/brand/intervantis-mark.png" alt="Dom" className="w-full h-full object-contain animate-dom-spin" draggable="false" />
              </div>
            </div>
            <div className="min-w-0">
              <div className="font-display font-medium text-white text-sm leading-tight">{t('assistant.title')}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="label-mono text-emerald-400/80 text-[9px]">{t('assistant.online')}</span>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="ml-auto w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              m.role === 'cta' ? (
                <div key={i} data-testid="chat-team-cta" className="animate-msg-in rounded-2xl border border-ember/30 bg-ember/[0.08] p-4">
                  <p className="font-body text-sm text-white/85 leading-relaxed">{t('assistant.teamPrompt')}</p>
                  <button
                    data-testid="chat-quote-btn"
                    onClick={() => { onQuote?.(); setOpen(false); }}
                    className="group mt-3 inline-flex items-center gap-2 rounded-full bg-ember hover:bg-ember-hover text-white px-4 py-2.5 label-mono text-xs shadow-lg shadow-ember/25 transition-colors"
                  >
                    {t('assistant.teamCta')}
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                  </button>
                </div>
              ) : (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} animate-msg-in`}>
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[82%] rounded-2xl px-4 py-2.5 font-body text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-ember text-white rounded-br-md shadow-lg shadow-ember/20'
                        : 'bg-white/[0.07] text-white/90 border border-white/10 rounded-bl-md'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              )
            ))}
            {loading && (
              <div className="flex justify-start animate-msg-in">
                <div className="bg-white/[0.07] border border-white/10 rounded-2xl rounded-bl-md px-4 py-3.5 flex items-center gap-1.5">
                  <span className="dom-typing-dot w-1.5 h-1.5 rounded-full bg-ember" style={{ animationDelay: '0ms' }} />
                  <span className="dom-typing-dot w-1.5 h-1.5 rounded-full bg-ember" style={{ animationDelay: '200ms' }} />
                  <span className="dom-typing-dot w-1.5 h-1.5 rounded-full bg-ember" style={{ animationDelay: '400ms' }} />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-3 bg-white/[0.02]">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder={t('assistant.placeholder')}
                className="flex-1 resize-none bg-white/[0.05] border border-white/12 focus:border-ember focus:bg-white/[0.07] rounded-2xl px-4 py-2.5 text-white font-body text-sm outline-none transition-all placeholder:text-white/30 max-h-28"
              />
              <button
                data-testid="chat-send"
                onClick={send}
                disabled={loading || !input.trim()}
                className="shrink-0 w-10 h-10 rounded-full bg-ember hover:bg-ember-hover disabled:opacity-40 disabled:hover:bg-ember text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                aria-label={t('assistant.send')}
              >
                <Send size={17} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
